/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: moduleIds.ts
 🗓️ Created: 2025-01-13
 🧩 Description:
    Module slug constants and helpers for permission checking.
    Now uses slugs directly without fetching numeric IDs from DB.

 🔁 Last Updated: 2025-02-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

// Module slugs mapping - direct string identifiers (no numeric resolution needed)
export const MODULE_SLUGS = {
  // === PBX Extensions ===
  EXTENSIONS: 'pbx_extensions_extensions',
  HOT_DESKING: 'pbx_extensions_hot_desking',
  BULK_MODIFICATION: 'pbx_extensions_bulk_modification',
  EXTENSION_STATUS: 'pbx_extensions_status',

  // === PBX Trunks ===
  TRUNKS: 'pbx_trunks_trunks',

  // === PBX Call Center ===
  RING_GROUPS: 'pbx_call_center_ring_groups',
  QUEUES: 'pbx_call_center_queues',
  CALLBACK_PROFILES: 'pbx_call_center_callback_profiles',
  VIP_LISTS: 'pbx_call_center_vip_lists',
  QUEUE_PRIORITIES: 'pbx_call_center_queue_priorities',
  AGENTS: 'pbx_call_center_agents', // Extra if needed

  // === PBX Applications ===
  CONFERENCES: 'pbx_applications_conferences',
  CUSTOM_APPLICATIONS: 'pbx_applications_custom_applications',
  CUSTOM_DESTINATION: 'pbx_applications_custom_destination',
  CUSTOM_CONTEXT: 'pbx_applications_custom_context',
  FEATURE_CODES: 'pbx_applications_feature_codes',
  PAGING_GROUPS: 'pbx_applications_paging',
  PICKUP_GROUPS: 'pbx_applications_pickup',
  PARKING_LOTS: 'pbx_applications_parking',
  SPEED_DIALS: 'pbx_applications_speed_dials',
  VOICEMAIL_BROADCAST: 'pbx_applications_voicemail_broadcast',
  CALLBACK_RULES: 'pbx_applications_callback',
  DISA_ENTRIES: 'pbx_applications_disa',
  PIN_LISTS: 'pbx_routing_pin_list',
  DYNAMIC_DESTINATIONS: 'pbx_applications_dynamic_destination',
  DIRECT_DIAL: 'pbx_applications_direct_dial',
  DIRECT_ROUTE: 'pbx_applications_direct_route',
  DIAL_PROFILES: 'pbx_applications_dial_profiles',
  CALL_FLOWS: 'pbx_tools_call_flows', // sometimes called out as distinct

  // === PBX Class of Service ===
  COS_CLASS: 'pbx_cos_class',
  COS_FEATURE_CATEGORIES: 'pbx_cos_feature_categories',
  COS_DIAL_RESTRICTION: 'pbx_cos_dial_restriction',
  COS_CUSTOMER_CODES: 'pbx_cos_customer_codes',
  COS_AUTH_CODES: 'pbx_cos_auth_codes',
  COS_ROUTE_SELECTIONS: 'pbx_cos_route_selections',

  // === PBX Routing ===
  OUTBOUND_ROUTES: 'pbx_routing_outbound',
  INBOUND_ROUTES: 'pbx_routing_inbound',
  DYNAMIC_ROUTING: 'pbx_routing_dynamic',
  GATEWAYS: 'pbx_routing_gateways',
  TELEPHONY_SERVERS: 'admin_telephony_servers', // legacy alias
  PBX_CALLS_ROUTING_GATEWAYS: 'pbx_routing_gateways',

  // === PBX Provisioning ===
  PROVISIONING_FIRMWARE: 'pbx_provisioning_firmware',
  PROVISIONING_TEMPLATES: 'pbx_provisioning_templates',
  PROVISIONING_DEVICES: 'pbx_provisioning_provisioning',

  // === PBX Tools ===
  BLACKLIST: 'pbx_tools_blacklist',
  DASHBOARD: 'pbx_tools_dashboard',
  LOG_VIEWER: 'pbx_tools_log_viewer',
  CRON_PROFILES: 'pbx_tools_cron',
  WEAK_PASSWORDS: 'pbx_tools_weak_password',
  PHONEBOOKS: 'pbx_tools_phonebooks',
  TASK_MANAGER: 'pbx_tools_task_manager',
  TIME_GROUPS: 'pbx_tools_time_groups',
  TIME_CONDITIONS: 'pbx_tools_time_conditions',
  ANNOUNCEMENTS: 'pbx_tools_announcements',
  LANGUAGES: 'pbx_tools_languages',
  CID_MODIFIERS: 'pbx_tools_cid_modifiers',
  CID_LOOKUP: 'pbx_tools_cid_lookup',
  TOOLS_AUTH_CODES: 'pbx_tools_auth_codes',
  VOICE_GUIDES: 'pbx_tools_voice_guides',
  IVRS: 'pbx_tools_ivr',
  PBX_CLI: 'pbx_tools_cli',

  // === PBX Fax / Hotel / Emergency ===
  VIRTUAL_FAX: 'pbx_fax_devices',
  HOTEL_MANAGEMENT: 'pbx_hotel_management',
  HOTEL_SETTINGS: 'pbx_hotel_settings',
  EMERGENCY_NUMBERS: 'pbx_emergency_numbers',
  EMERGENCY_LOCATIONS: 'pbx_emergency_locations',

  // === Reports ===
  CALL_DETAIL_RECORDS: 'reports_cdr_cdr',
  CDR_FILTERS: 'reports_cdr_filters',
  PBX_SIP_ENDPOINTS: 'reports_pbx_sip_endpoints',
  AUDIT_LOGS: 'reports_system_audit_logs',
  USER_ACTIVITY: 'reports_system_user_activity',
  CHANGE_HISTORY: 'reports_system_change_history',
  SECURITY_LOGS: 'reports_system_security',

  // === PBX Settings ===
  SYSTEM_GENERAL: 'settings_pbx_system_general',
  SYSTEM_VARS: 'settings_pbx_system_vars',
  CONFERENCE_PROFILES: 'settings_pbx_conference_profiles',
  QUEUE_PROFILES: 'settings_pbx_queue_profiles',
  ESL_USERS: 'settings_pbx_esl_users',
  MICROSOFT_TEAMS: 'settings_pbx_microsoft_teams',
  LOG_SETTINGS: 'settings_pbx_log_settings',
  MINI_HTTP: 'settings_pbx_mini_http',
  CDR_SETTINGS: 'settings_pbx_cdr_settings',
  VOICEMAIL_SETTINGS: 'settings_pbx_voicemail_settings',

  // === Voice Prompts / Tech Settings ===
  VOICE_MOH: 'settings_voice_music_hold',
  VOICE_RECORDING: 'settings_voice_recording',
  TECH_DIAL_PROFILES: 'settings_tech_dial_profiles',
  SIP_SETTINGS: 'settings_tech_sip',
  CRM_INTEGRATIONS: 'settings_crm_integrations',
  INTEGRATIONS: 'settings_integrations_config',

  // === System Admin ===
  USERS: 'admin_admin_users',
  ROLE_PROFILES: 'admin_admin_user_profiles',
  LOG_PROFILES: 'admin_admin_log_profiles',
  API_KEYS: 'admin_admin_app_keys',
  SYSTEM_MISC: 'admin_system_misc',
  EMAIL_SETTINGS: 'admin_system_email_settings',
  EMAIL_TEMPLATES: 'admin_system_email_template',
  CERTIFICATES: 'admin_system_certificates',
  HTTP_SERVER: 'admin_system_http_server',
  BRANDING: 'admin_system_branding',
  TENANTS: 'admin_multi_tenant_tenants',
  TELEPHONY_DOMAINS: 'admin_multi_tenant_domains',
  TENANT_BRANDING: 'admin_multi_tenant_branding',
  ACCESS_CONTROL: 'admin_firewall_access_control',
  FIREWALL_SERVICES: 'admin_firewall_services',
  FIREWALL_RULES: 'admin_firewall_rules',
  GEO_FIREWALL: 'admin_firewall_geo',
  OPENVPN_SERVER: 'admin_network_openvpn_server',
  OPENVPN_CLIENT: 'admin_network_openvpn_client',
  ADDONS: 'admin_addons_addons',
  BACKUP_RESTORE: 'admin_maintenance_backup',
  MAINTENANCE: 'admin_maintenance_maintenance',
  ADMIN_CRON_PROFILES: 'admin_maintenance_cron_profiles',
  ADMIN_CRON_JOBS: 'admin_maintenance_cron_jobs',

  // === Extra UI specifics / Fallbacks ===
  PROFILES: 'admin_admin_user_profiles', // Legacy
  APPEARANCE: 'settings_appearance', // Public
  USER_PROFILE: 'user_profile', // Public
  SETTINGS_PBX_SYSTEM_VARS: 'settings_pbx_system_vars',
  SETTINGS_VOICE_PROMPTS_PBX_GUIDE: 'settings_voice_pbx_guide',
  SETTINGS_VOICE_PROMPTS_RECORDING: 'settings_voice_recording',
  SETTINGS_VOICE_PROMPTS_MOH: 'settings_voice_moh',
  SETTINGS_TECH_DIAL_PROFILE: 'settings_tech_dial_profiles',
  
  // === AI System ===
  AI_PROVIDERS: 'admin_ai_providers',
  AI_PROFILES: 'admin_ai_profiles',
  AI_AGENTS: 'admin_ai_agents',
  AI_TOOL_PROFILES: 'admin_ai_tool_profiles',
  AI_TRANSLATOR_PROFILES: 'admin_ai_integration_translator_profiles',
} as const;

/**
 * Resolve a module identifier to its slug.
 * Accepts either a MODULE_SLUGS key (e.g. 'USERS') or a direct slug string.
 * No API calls needed — pure synchronous lookup.
 */
export const resolveModuleSlug = (identifier: string): string | null => {
  // First, try as a MODULE_SLUGS key (e.g. 'USERS' -> 'admin_admin_users')
  if (identifier in MODULE_SLUGS) {
    return MODULE_SLUGS[identifier as keyof typeof MODULE_SLUGS];
  }

  // Otherwise, treat it as a direct slug string (pass through)
  return identifier;
};

// Legacy compat: these are no-ops now since we use slugs directly
export const resolveModuleId = resolveModuleSlug;
export const getModuleId = (slug: string): string | null => slug || null;
export const getModuleIdByKey = (key: keyof typeof MODULE_SLUGS): string | null => MODULE_SLUGS[key] || null;

// No-op service (no longer needed, but kept for compat)
class ModuleIdsService {
  isServiceLoaded(): boolean { return true; }
  async loadModuleIds(): Promise<void> { /* no-op */ }
  async forceReload(): Promise<void> { /* no-op */ }
  clearCache(): void { /* no-op */ }
}

export const moduleIdsService = new ModuleIdsService();
export const forceReloadModuleIds = () => moduleIdsService.forceReload();

export default moduleIdsService;
